import { getServerApplicationContext } from '@hc-frontend/core-context';
import type { FeaturedAd } from '@hc-frontend/core-types';
import { AdServer } from '@hc-frontend/core-types';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';
import { randomUUID } from 'crypto';

import {
  QuinStreetListingDto,
  type QuinStreetRequestBody,
  QuinStreetResponseDto,
} from './types';

/**
 * Partner-supplied URL as an absolute `https:` URL (protocol-relative ones are
 * completed); anything else, e.g. `http:` or `javascript:`, is rejected.
 */
function toHttpsUrl(raw?: string): string | undefined {
  const value = raw?.trim();
  if (!value) return undefined;

  try {
    const url = new URL(value.startsWith('//') ? `https:${value}` : value);
    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function mapListing(listing: QuinStreetListingDto): FeaturedAd | undefined {
  const link = toHttpsUrl(listing.clickurl);
  const logo = toHttpsUrl(listing.logo);
  // No safe destination or logo means the listing cannot be shown.
  if (!link || !logo) return undefined;

  return {
    adsSource: AdServer.enum.NextInsure,
    id: randomUUID(),
    logo,
    title: listing.title,
    content: listing.description,
    provider: {
      id: undefined,
      name: listing.displayname,
      displayUrl: listing.sitehost,
    },
    link,
    creativeId: listing.vendorKey,
    groupId: undefined,
    campaignId: undefined,
    campaignName: undefined,
    dealType: 'CPC',
    cpc: listing.cpc,
    impressionPixelUrl: toHttpsUrl(listing.imp_pixel),
  };
}

function logFailure(message: string, rootCause: Error) {
  getServerApplicationContext(fetchQuinStreetAdsInternal).log({
    type: 'error',
    context: 'server',
    message,
    data: { rootCause },
  });
}

/**
 * POSTs the datapass body to the QuinStreet listings API and maps the
 * listings to `FeaturedAd`, sorted by QuinStreet's `rank`.
 * Returns no ads on any failure, including when `timeoutMs` elapses.
 * The request body carries lead PII, so it is never logged.
 */
export async function fetchQuinStreetAdsInternal(
  url: string,
  body: QuinStreetRequestBody,
  { timeoutMs }: { timeoutMs?: number } = {},
): Promise<FeaturedAd[]> {
  try {
    const response = await fetchBasedCall<QuinStreetRequestBody, unknown>({
      url,
      method: 'POST',
      body,
      cache: 'no-store',
      ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
    });

    if (response.result !== 'success') {
      logFailure(
        'failedToGetQuinStreetAds',
        new Error(`QuinStreet responded with status ${response.status}`),
      );
      return [];
    }

    const parsed = QuinStreetResponseDto.safeParse(response.body);
    if (!parsed.success) {
      logFailure(
        'failedToGetQuinStreetAds',
        new Error('Unexpected QuinStreet response shape'),
      );
      return [];
    }

    const { listing = [] } = parsed.data.response.listingset;
    const listings = listing.flatMap((item) => {
      const parsedListing = QuinStreetListingDto.safeParse(item);
      return parsedListing.success ? [parsedListing.data] : [];
    });

    return listings
      .sort((a, b) => a.rank - b.rank)
      .flatMap((item) => mapListing(item) ?? []);
  } catch (error) {
    const timedOut = error instanceof Error && error.name === 'TimeoutError';

    logFailure(
      timedOut ? 'quinStreetAdsTimeout' : 'failedToGetQuinStreetAds',
      new Error(
        timedOut
          ? `QuinStreet ads timed out after ${timeoutMs}ms`
          : 'Failed to fetch QuinStreet ads',
      ),
    );
  }

  return [];
}
